// File: team/shared/hooks/useTeamProgressTracker.js

import { useState, useEffect, useCallback } from 'react';
import { useTeam } from 'team/contexts/teamContext';
import { getTemplates } from 'team/apis/teamTemplateApi';
import { getTeamIntegrations } from 'team/apis/teamIntegrationApi';

const useTeamProgressTracker = () => {
    const { currentTeamId, getCurrentTeam, getTeamStats, teamStats } = useTeam();
    const team = getCurrentTeam();

    // Initialize states
    const [createTeam, setCreateTeam] = useState(true);
    const [addCompanyLogo, setAddCompanyLogo] = useState(false);
    const [addMembers, setAddMembers] = useState(false);
    const [createTemplate, setCreateTemplate] = useState(false);
    const [setupCrmIntegration, setSetupCrmIntegration] = useState(false);
    const [completedSteps, setCompletedSteps] = useState(0);

    // Method to count completed steps
    useEffect(() => {
        let steps = 0;
        if (createTeam) steps++;
        if (addCompanyLogo) steps++;
        if (addMembers) steps++;
        if (createTemplate) steps++;
        if (setupCrmIntegration) steps++;
        setCompletedSteps(steps);
    }, [createTeam, addCompanyLogo, addMembers, createTemplate, setupCrmIntegration]);

    // Method to check if company logo has been added
    const checkAddCompanyLogo = useCallback(async (teamData) => {
        try {
            if (!teamData) return false;
            
            const settings = teamData.settings || {};
            const hasLogo = !!settings.companyLogoUrl;
            
            setAddCompanyLogo(hasLogo);
            return hasLogo;
        } catch (error) {
            console.error('Error checking company logo status:', error);
            return false;
        }
    }, []);

    // Method to check if team members have been added
    const checkAddMembers = useCallback(async (teamId) => {
        try {
            if (!teamId) return false;
            
            // Get stats from context or fetch them
            let stats = teamStats[teamId];
            if (!stats) {
                const result = await getTeamStats(teamId);
                if (result.success) {
                    stats = result.data;
                }
            }
            
            // Consider members added if totalMembers > 1 (Admin + at least one more)
            const hasMembers = stats && stats.totalMembers > 1;
            setAddMembers(hasMembers);
            return hasMembers;
        } catch (error) {
            console.error('Error checking team members status:', error);
            return false;
        }
    }, [teamStats, getTeamStats]);

    // Method to check if template has been created
    const checkCreateTemplate = useCallback(async (teamId) => {
        try {
            if (!teamId) return false;
            
            const result = await getTemplates(teamId, { page: 1, limit: 1 });
            const hasTemplates = result && result.templates && result.templates.length > 0;
            
            setCreateTemplate(hasTemplates);
            return hasTemplates;
        } catch (error) {
            console.error('Error checking team templates status:', error);
            return false;
        }
    }, []);

    // Method to check if CRM integration has been set up
    const checkSetupCrmIntegration = useCallback(async (teamId) => {
        try {
            if (!teamId) return false;
            
            const result = await getTeamIntegrations(teamId);
            // result is { success: true, integrations: [...] }
            const hasIntegration = result && result.integrations && Array.isArray(result.integrations) && result.integrations.length > 0;
            
            setSetupCrmIntegration(hasIntegration);
            return hasIntegration;
        } catch (error) {
            console.error('Error checking CRM integration status:', error);
            return false;
        }
    }, []);

    // Method to update all progress checks
    const updateAllProgress = useCallback(async (teamId) => {
        if (!teamId || !team) return;
        
        try {
            await Promise.all([
                checkAddCompanyLogo(team),
                checkAddMembers(teamId),
                checkCreateTemplate(teamId),
                checkSetupCrmIntegration(teamId)
            ]);
        } catch (error) {
            console.error('Error updating team progress:', error);
        }
    }, [team, checkAddCompanyLogo, checkAddMembers, checkCreateTemplate, checkSetupCrmIntegration]);

    // Update progress when team ID or data changes
    useEffect(() => {
        if (currentTeamId && team) {
            updateAllProgress(currentTeamId);
        }
    }, [currentTeamId, team, updateAllProgress]);

    return {
        createTeam,
        addCompanyLogo,
        addMembers,
        createTemplate,
        setupCrmIntegration,
        completedSteps,
        updateAllProgress
    };
};

export default useTeamProgressTracker;
